/**
 * Utilidades específicas del Dashboard de IVA: cálculo por período + detección de estado de pago.
 *
 * Distinto de src/utils/calcularImpuestos.js — aquel genera entries para el panel de
 * proyecciones futuras de FlujoCajaV2 (filtra solo períodos pendientes).
 * Este genera la grilla histórica + proyectada del dashboard, incluyendo períodos pagados
 * y vencidos.
 */

const MESES_LABEL = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const TIPOS_AFECTOS_PPM = [33, 56];

const capitalizarMes = (periodo) => {
  if (!periodo) return "";
  const [yyyy, mm] = periodo.split("-");
  return `${MESES_LABEL[parseInt(mm, 10) - 1]} ${yyyy}`;
};

/**
 * Genera array de períodos de IVA para el dashboard. Cubre históricos (mesesAtras) y
 * proyectados (mesesAdelante). Períodos sin datos SII se proyectan con el promedio de los
 * últimos 3 períodos con datos.
 *
 * Cada período retorna:
 *   { periodo, mesLabel, ivaDebito, ivaCredito, ivaNeto, ivaAPagar, ivaAFavor,
 *     ventasNetas, ppm, totalAPagar, fechaPago, estimado }
 *
 * - ivaDebito: suma monto_iva de emitidas. NC (61) restan.
 * - ivaCredito: suma monto_iva de recibidas. NC (61) restan.
 * - ivaNeto = débito - crédito. Puede ser negativo (remanente a favor).
 * - ivaAPagar = max(ivaNeto, 0). Si negativo, se reporta también como ivaAFavor.
 * - ventasNetas: suma monto_neto de emitidas afectas (DTE 33+56). Excluye exenta (34),
 *   NC (61), exportación (110).
 * - ppm = ventasNetas * tasaPPM.
 * - totalAPagar = ivaAPagar + ppm.
 * - fechaPago: día 12 del mes siguiente al período.
 * - estimado: true si el período no tiene datos SII y se usaron promedios.
 *
 * @param {Array} emitidas - facturas_sii_emitidas
 * @param {Array} recibidas - facturas_sii_recibidas
 * @param {Object} options
 * @param {number} options.tasaPPM - decimal (0.02 = 2% régimen general)
 * @param {number} options.mesesAtras - cantidad de períodos previos al actual (no incluye el actual)
 * @param {number} options.mesesAdelante - cantidad de períodos desde el actual incluido
 * @returns {Array} períodos en orden cronológico (más antiguo primero)
 */
export const calcularPeriodosIVA = (
  emitidas,
  recibidas,
  { tasaPPM = 0.02, mesesAtras = 6, mesesAdelante = 3 } = {}
) => {
  const hoy = new Date();

  const ivaDebitoPorMes = {};
  const ivaCreditoPorMes = {};
  const ventasNetasPorMes = {};

  for (const f of emitidas || []) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    const signo = f.tipo_dte === 61 ? -1 : 1;
    ivaDebitoPorMes[mes] = (ivaDebitoPorMes[mes] || 0) + signo * Number(f.monto_iva || 0);
    ventasNetasPorMes[mes] =
      (ventasNetasPorMes[mes] || 0) + (TIPOS_AFECTOS_PPM.includes(f.tipo_dte) ? Number(f.monto_neto || 0) : 0);
  }

  for (const f of recibidas || []) {
    const mes = f.periodo_tributario;
    if (!mes) continue;
    const signo = f.tipo_dte === 61 ? -1 : 1;
    ivaCreditoPorMes[mes] = (ivaCreditoPorMes[mes] || 0) + signo * Number(f.monto_iva || 0);
  }

  // Lista de períodos (orden cronológico)
  const periodos = [];
  for (let offset = -mesesAtras; offset < mesesAdelante; offset++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() + offset, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    periodos.push(`${yyyy}-${mm}`);
  }

  // Promedio para proyección de períodos sin datos
  const periodosConDatos = Object.keys(ivaDebitoPorMes)
    .filter((p) => ivaDebitoPorMes[p] !== 0)
    .sort();
  const ultimos3 = periodosConDatos.slice(-3);
  const sumaIVANetoUltimos3 = ultimos3.reduce(
    (s, p) => s + Math.max((ivaDebitoPorMes[p] || 0) - (ivaCreditoPorMes[p] || 0), 0),
    0
  );
  const sumaPPMUltimos3 = ultimos3.reduce((s, p) => s + Math.round((ventasNetasPorMes[p] || 0) * tasaPPM), 0);
  const promedioIVA = ultimos3.length > 0 ? Math.round(sumaIVANetoUltimos3 / ultimos3.length) : 0;
  const promedioPPM = ultimos3.length > 0 ? Math.round(sumaPPMUltimos3 / ultimos3.length) : 0;

  return periodos.map((periodo) => {
    const tieneDatos = periodo in ivaDebitoPorMes || periodo in ivaCreditoPorMes;
    const [yyyy, mm] = periodo.split("-");
    const mesSig = parseInt(mm, 10) === 12 ? 1 : parseInt(mm, 10) + 1;
    const añoSig = parseInt(mm, 10) === 12 ? parseInt(yyyy, 10) + 1 : parseInt(yyyy, 10);
    const fechaPago = `${añoSig}-${String(mesSig).padStart(2, "0")}-12`;

    let ivaDebito = 0;
    let ivaCredito = 0;
    let ventasNetas = 0;
    let ppm = 0;
    let estimado = false;
    let ivaNeto = 0;

    if (tieneDatos) {
      ivaDebito = ivaDebitoPorMes[periodo] || 0;
      ivaCredito = ivaCreditoPorMes[periodo] || 0;
      ventasNetas = ventasNetasPorMes[periodo] || 0;
      ppm = Math.round(ventasNetas * tasaPPM);
      ivaNeto = ivaDebito - ivaCredito;
    } else {
      ppm = promedioPPM;
      ivaNeto = promedioIVA;
      estimado = true;
    }

    const ivaAPagar = Math.max(ivaNeto, 0);
    const ivaAFavor = ivaNeto < 0 ? -ivaNeto : 0;
    const totalAPagar = ivaAPagar + ppm;

    return {
      periodo,
      mesLabel: capitalizarMes(periodo),
      ivaDebito,
      ivaCredito,
      ivaNeto,
      ivaAPagar,
      ivaAFavor,
      ventasNetas,
      ppm,
      totalAPagar,
      fechaPago,
      estimado,
    };
  });
};

/**
 * Detecta si un período de IVA fue pagado buscando en movimientos_caja un asiento con
 * categoria_pago='pago_impuesto' cuya descripcion contenga el período. El modal de Pago
 * Institucional guarda la descripción como "{Institución} — {Mes} {Año} ({notas})", por
 * ejemplo "SII — Declaración mensual — Marzo 2026". También admite el formato YYYY-MM por
 * compatibilidad.
 *
 * Si el período no está pagado, retorna estado 'vencido' o 'por_pagar' según la fechaPago
 * esperada vs hoy.
 *
 * @param {string} periodo - YYYY-MM
 * @param {string} fechaPagoEsperada - YYYY-MM-DD (típicamente día 12 mes siguiente)
 * @param {Array} movimientosCaja - array con {id, fecha, monto, descripcion, categoria_pago}
 * @returns {{estado: 'pagado'|'vencido'|'por_pagar', fechaPago?, montoPago?, movimientoIds?}}
 */
export const detectarEstadoPago = (periodo, fechaPagoEsperada, movimientosCaja) => {
  const hoy = new Date().toISOString().split("T")[0];
  const [yyyy, mm] = periodo.split("-");
  const mesNombre = MESES_LABEL[parseInt(mm, 10) - 1];
  const variantes = [periodo, `${mesNombre} ${yyyy}`];

  const candidatos = (movimientosCaja || []).filter(
    (m) => m.categoria_pago === "pago_impuesto" && variantes.some((v) => (m.descripcion || "").includes(v))
  );

  if (candidatos.length > 0) {
    const montoTotal = candidatos.reduce((s, m) => s + Number(m.monto || 0), 0);
    const fechaUltima = candidatos.reduce((max, m) => (m.fecha > max ? m.fecha : max), "");
    return {
      estado: "pagado",
      fechaPago: fechaUltima,
      montoPago: montoTotal,
      movimientoIds: candidatos.map((m) => m.id),
    };
  }

  if (fechaPagoEsperada < hoy) return { estado: "vencido" };
  return { estado: "por_pagar" };
};
